import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { CONFIG } from '~/_shared/constants/config';
import { MapPageSplitScreenTab } from '~/map/page/splitScreen/mapPageSplitScreen.types';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import {
  userSettingsSplitPanelClose, userSettingsSplitPanelOpen,
  userSettingsSplitPanelSetTab,
} from '~/store/userData/settings/userDataSettings.actionCreators';
import { useSplitItemSettingsSelector } from '~/store/userData/settings/userDataSettings.selectors';

export const useSplitPanelState = (mapId: number | null) => {
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();
  const splitItemSettings = useSplitItemSettingsSelector(clientId, mapId);

  const closePanel = useCallback(() => {
    if (mapId === null || clientId === null) {
      return;
    }

    dispatch(userSettingsSplitPanelClose(clientId, mapId));
  }, [dispatch, clientId, mapId]);

  const openPanel = useCallback(() => {
    if (mapId === null || clientId === null) {
      return;
    }

    dispatch(userSettingsSplitPanelOpen(clientId, mapId));
  }, [dispatch, clientId, mapId]);

  const handleTabChange = useCallback((newTab: MapPageSplitScreenTab) => {
    if (!clientId || !mapId) {
      return;
    }
    dispatch(userSettingsSplitPanelSetTab(clientId, mapId, newTab));
  }, [clientId, dispatch, mapId]);

  return useMemo(() => {
    const tab = CONFIG.ENABLE_CHARTS
      ? splitItemSettings.tab ?? MapPageSplitScreenTab.Spreadsheet
      : MapPageSplitScreenTab.Spreadsheet;

    return ({
      isActive: splitItemSettings.isActive,
      tab,
      openPanel,
      closePanel,
      onTabChange: handleTabChange,
    });
  }, [closePanel, handleTabChange, openPanel, splitItemSettings.isActive, splitItemSettings.tab]);
};
